import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  InputNumber,
  Progress,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd'
import type { UploadFile, UploadProps } from 'antd'
import {
  CopyOutlined,
  DownloadOutlined,
  FileTextOutlined,
  TranslationOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import {
  DEFAULT_ASS_CHUNK_CUES,
  MAX_SUBTITLE_CHARS,
  SUBTITLE_ACCEPT,
  TRANSLATE_SUBTITLE_TIMEOUT_MS,
  buildFallbackTranslatedFilename,
  formatByteSize,
  inferSubtitleFormat,
  mergeAssSubtitleChunks,
  parseAnimeIdInput,
  parseContentDispositionFilename,
  splitAssSubtitleByDialogue,
} from '@/lib/subtitle-translate'
import type { SubtitleFormat } from '@/lib/subtitle-translate'
import { useDebounced, useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/subtitle-translate')({
  component: SubtitleTranslatePage,
})

const FORMAT_OPTIONS: Array<{ value: SubtitleFormat; label: string }> = [
  { value: 'ass', label: 'ASS' },
  { value: 'srt', label: 'SRT' },
  { value: 'vtt', label: 'VTT' },
]

const TARGET_LANG_OPTIONS = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁体中文' },
]

const ERROR_LABELS: Record<string, string> = {
  unauthorized: '登录已失效',
  forbidden: '当前账号不是管理员',
  auth_unavailable: '认证服务不可用',
  admin_check_unavailable: '管理员校验不可用',
  method_not_allowed: '请求方法不支持',
  invalid_json: '请求 JSON 异常',
  invalid_request: '请求参数不完整',
  subtitle_too_large: '字幕超过 100 万字符',
  no_cues: '没有识别到字幕条目',
  too_many_cues: '字幕条目超过 3000 条',
  context_unavailable: '读取番剧资料失败',
  anime_not_found: 'anime id 不存在',
  not_configured: '翻译模型密钥未配置',
  timeout: '翻译模型超时',
  parse_error: '模型返回不是合法 JSON',
  bad_shape: '模型返回结构异常',
  structure_mismatch: '模型返回条目数不一致',
  fetch_error: '翻译模型请求失败',
  http_error: '翻译模型返回错误',
  body_not_json: '翻译模型响应不是 JSON',
  no_content: '翻译模型没有返回内容',
  internal_error: '边缘函数内部错误',
}

type TranslateResult = {
  text: string
  filename: string
  cueCount: number | null
  chunkCount: number
  model: string | null
}

type TranslateInvokeResult = Omit<TranslateResult, 'chunkCount'>

type AnimeOption = {
  id: number
  title: string
}

type TranslateProgress = {
  phase: 'reading' | 'splitting' | 'translating' | 'merging' | 'done'
  totalChunks: number
  completedChunks: number
  activeChunk: number | null
  activeCueCount: number | null
}

function SubtitleTranslatePage() {
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [animeId, setAnimeId] = useState<number | null>(null)
  const [animeSearchInput, setAnimeSearchInput] = useState('')
  const [format, setFormat] = useState<SubtitleFormat>('ass')
  const [targetLang, setTargetLang] = useState('zh-CN')
  const [assChunkCues, setAssChunkCues] = useState(DEFAULT_ASS_CHUNK_CUES)
  const [subtitleFile, setSubtitleFile] = useState<File | null>(null)
  const [sourceCharCount, setSourceCharCount] = useState<number | null>(null)
  const [result, setResult] = useState<TranslateResult | null>(null)
  const [progress, setProgress] = useState<TranslateProgress | null>(null)

  const fileList = useMemo<UploadFile[]>(
    () =>
      subtitleFile
        ? [
            {
              uid: `${subtitleFile.name}-${subtitleFile.lastModified}`,
              name: subtitleFile.name,
              size: subtitleFile.size,
              status: 'done',
            },
          ]
        : [],
    [subtitleFile],
  )

  const hasValidAnimeId =
    typeof animeId === 'number' && Number.isInteger(animeId) && animeId > 0
  const debouncedAnimeSearch = useDebounced(animeSearchInput.trim(), 300)

  const progressPercent =
    progress && progress.totalChunks > 0
      ? Math.round((progress.completedChunks / progress.totalChunks) * 100)
      : 0

  const translateMutation = useMutation({
    mutationFn: async () => {
      if (!subtitleFile) throw new Error('请先上传字幕文件')
      const checkedAnimeId = animeId
      if (!hasValidAnimeId || checkedAnimeId == null) {
        throw new Error('请输入合法的 anime id')
      }

      setResult(null)
      setProgress({
        phase: 'reading',
        totalChunks: 0,
        completedChunks: 0,
        activeChunk: null,
        activeCueCount: null,
      })

      const subtitle = await subtitleFile.text()
      setSourceCharCount(subtitle.length)

      if (!subtitle.trim()) throw new Error('字幕文件为空')
      if (subtitle.length > MAX_SUBTITLE_CHARS) {
        throw new Error(`字幕超过 ${MAX_SUBTITLE_CHARS.toLocaleString()} 字符`)
      }

      if (format === 'ass') {
        setProgress({
          phase: 'splitting',
          totalChunks: 0,
          completedChunks: 0,
          activeChunk: null,
          activeCueCount: null,
        })

        const perChunk = validAssChunkCues(assChunkCues)
        const chunks = splitAssSubtitleByDialogue(subtitle, perChunk)
        if (chunks.length === 0) throw new Error('ASS 字幕没有 Dialogue 行')

        const translatedChunks: string[] = []
        let filename: string | null = null
        let model: string | null = null
        let cueCount = 0

        for (let i = 0; i < chunks.length; i += 1) {
          const chunk = chunks[i]
          setProgress({
            phase: 'translating',
            totalChunks: chunks.length,
            completedChunks: i,
            activeChunk: chunk.index,
            activeCueCount: chunk.dialogueCount,
          })

          const translated = await invokeTranslateSubtitle({
            animeId: checkedAnimeId,
            format: 'ass',
            targetLang,
            subtitle: chunk.text,
            originalFileName: subtitleFile.name,
          })

          translatedChunks.push(translated.text)
          filename ??= translated.filename
          model ??= translated.model
          cueCount += translated.cueCount ?? chunk.dialogueCount

          const hasNextChunk = i + 1 < chunks.length
          const nextChunk = chunks[i + 1]
          setProgress({
            phase: 'translating',
            totalChunks: chunks.length,
            completedChunks: i + 1,
            activeChunk: hasNextChunk ? nextChunk.index : null,
            activeCueCount: hasNextChunk ? nextChunk.dialogueCount : null,
          })
        }

        setProgress({
          phase: 'merging',
          totalChunks: chunks.length,
          completedChunks: chunks.length,
          activeChunk: null,
          activeCueCount: null,
        })

        const merged = mergeAssSubtitleChunks(translatedChunks)
        setProgress({
          phase: 'done',
          totalChunks: chunks.length,
          completedChunks: chunks.length,
          activeChunk: null,
          activeCueCount: null,
        })

        return {
          text: merged,
          filename:
            filename ??
            buildFallbackTranslatedFilename(
              subtitleFile.name,
              targetLang,
              format,
            ),
          cueCount,
          chunkCount: chunks.length,
          model,
        } satisfies TranslateResult
      }

      setProgress({
        phase: 'translating',
        totalChunks: 1,
        completedChunks: 0,
        activeChunk: 1,
        activeCueCount: null,
      })

      const translated = await invokeTranslateSubtitle({
        animeId: checkedAnimeId,
        format,
        targetLang,
        subtitle,
        originalFileName: subtitleFile.name,
      })

      setProgress({
        phase: 'done',
        totalChunks: 1,
        completedChunks: 1,
        activeChunk: null,
        activeCueCount: null,
      })

      return {
        ...translated,
        chunkCount: 1,
      } satisfies TranslateResult
    },
    onSuccess: (nextResult) => {
      setResult(nextResult)
      message.success(
        `翻译完成${nextResult.cueCount ? `：${nextResult.cueCount} 条` : ''}`,
      )
    },
    onError: (error: Error) => {
      message.error(`翻译失败：${error.message}`)
    },
  })

  const animeOptionsQuery = useQuery({
    queryKey: ['admin_subtitle_translate_anime_search', debouncedAnimeSearch],
    enabled: debouncedAnimeSearch.length >= 1,
    queryFn: async () => {
      const directAnimeId = parseAnimeIdInput(debouncedAnimeSearch)
      if (directAnimeId != null) {
        const { data, error } = await supabase
          .from('animes')
          .select('id, title')
          .eq('id', directAnimeId)
          .maybeSingle()
        if (error) throw error
        return data
          ? ([
              {
                id: data.id,
                title: data.title,
              },
            ] satisfies Array<AnimeOption>)
          : ([
              {
                id: directAnimeId,
                title: `Anime #${directAnimeId}`,
              },
            ] satisfies Array<AnimeOption>)
      }

      const { data, error } = await supabase
        .from('animes')
        .select('id, title')
        .ilike('search_title', `%${debouncedAnimeSearch}%`)
        .order('view_count', { ascending: false, nullsFirst: false })
        .limit(20)
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })

  const currentAnimeQuery = useQuery({
    queryKey: ['admin_subtitle_translate_current_anime', animeId],
    enabled: !!animeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('id, title')
        .eq('id', animeId!)
        .maybeSingle()
      if (error) throw error
      return data
    },
  })

  const animeSelectOptions: Array<AnimeOption> = useMemo(() => {
    const list = animeOptionsQuery.data ?? []
    const current = currentAnimeQuery.data
    if (current && !list.find((a) => a.id === current.id)) {
      return [current, ...list]
    }
    if (animeId && !list.find((a) => a.id === animeId)) {
      return [{ id: animeId, title: `Anime #${animeId}` }, ...list]
    }
    return list
  }, [animeId, animeOptionsQuery.data, currentAnimeQuery.data])

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const inferred = inferSubtitleFormat(file.name)
    if (!inferred) {
      message.warning('仅支持 .ass / .srt / .vtt 字幕')
      return false
    }

    setSubtitleFile(file)
    setFormat(inferred)
    setSourceCharCount(null)
    setResult(null)
    setProgress(null)
    return false
  }

  function clearFile() {
    setSubtitleFile(null)
    setSourceCharCount(null)
    setResult(null)
    setProgress(null)
  }

  async function copyResult() {
    if (!result) return
    await navigator.clipboard.writeText(result.text)
    message.success('已复制翻译文本')
  }

  function downloadResult() {
    if (!result) return

    const blob = new Blob([result.text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = result.filename
    document.body.append(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={4}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            字幕翻译
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
            上传 ASS / SRT / VTT 字幕，调用{' '}
            <Typography.Text code>admin-translate-subtitle</Typography.Text>{' '}
            返回可直接保存的中文字幕文本。anime id
            用于读取作品资料，保证专名一致。
          </Typography.Paragraph>
        </Space>
      </Card>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={8}>
          <Card
            title="输入"
            extra={<Tag color="blue">最大 100 万字符</Tag>}
            style={{ height: '100%' }}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Typography.Text strong>番剧</Typography.Text>
                <Select<number | undefined>
                  allowClear
                  showSearch
                  placeholder="输入关键字或 Anime ID"
                  value={animeId ?? undefined}
                  onChange={(value) => {
                    setAnimeId(value ?? null)
                    setResult(null)
                    setProgress(null)
                  }}
                  onClear={() => {
                    setAnimeId(null)
                    setAnimeSearchInput('')
                    setResult(null)
                    setProgress(null)
                  }}
                  onSearch={setAnimeSearchInput}
                  onInputKeyDown={(event) => {
                    if (event.key !== 'Enter') return
                    const directAnimeId = parseAnimeIdInput(animeSearchInput)
                    if (directAnimeId == null) return
                    setAnimeId(directAnimeId)
                    setResult(null)
                    setProgress(null)
                  }}
                  filterOption={false}
                  loading={
                    animeOptionsQuery.isFetching || currentAnimeQuery.isFetching
                  }
                  notFoundContent={
                    debouncedAnimeSearch
                      ? animeOptionsQuery.isFetching
                        ? '搜索中…'
                        : '无匹配'
                      : '输入关键字或 Anime ID'
                  }
                  optionLabelProp="label"
                  options={animeSelectOptions.map((anime) => ({
                    value: anime.id,
                    label: `${anime.title} (#${anime.id})`,
                  }))}
                  style={{ width: '100%' }}
                />
              </Space>

              <Row gutter={12}>
                <Col span={12}>
                  <Space
                    direction="vertical"
                    size={6}
                    style={{ width: '100%' }}
                  >
                    <Typography.Text strong>字幕格式</Typography.Text>
                    <Select
                      value={format}
                      onChange={setFormat}
                      options={FORMAT_OPTIONS}
                      style={{ width: '100%' }}
                    />
                  </Space>
                </Col>
                <Col span={12}>
                  <Space
                    direction="vertical"
                    size={6}
                    style={{ width: '100%' }}
                  >
                    <Typography.Text strong>目标语言</Typography.Text>
                    <Select
                      value={targetLang}
                      onChange={setTargetLang}
                      options={TARGET_LANG_OPTIONS}
                      style={{ width: '100%' }}
                    />
                  </Space>
                </Col>
              </Row>

              {format === 'ass' ? (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Typography.Text strong>每片 Dialogue 条数</Typography.Text>
                  <InputNumber
                    min={20}
                    max={1000}
                    precision={0}
                    controls={false}
                    value={assChunkCues}
                    disabled={translateMutation.isPending}
                    onChange={(value) =>
                      setAssChunkCues(
                        typeof value === 'number' && Number.isFinite(value)
                          ? value
                          : DEFAULT_ASS_CHUNK_CUES,
                      )
                    }
                    style={{ width: '100%' }}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    按本地脚本 CHUNK_CUES 逻辑逐片调用；只对 ASS 生效。
                  </Typography.Text>
                </Space>
              ) : null}

              <Upload.Dragger
                className="subtitle-translate-dragger"
                accept={SUBTITLE_ACCEPT}
                beforeUpload={beforeUpload}
                fileList={fileList}
                maxCount={1}
                multiple={false}
                onRemove={() => {
                  clearFile()
                  return true
                }}
                disabled={translateMutation.isPending}
              >
                <p className="ant-upload-drag-icon">
                  <FileTextOutlined />
                </p>
                <p className="ant-upload-text">选择字幕文件</p>
                <p className="ant-upload-hint">支持 .ass / .srt / .vtt</p>
              </Upload.Dragger>

              {subtitleFile ? (
                <Descriptions
                  size="small"
                  bordered
                  column={1}
                  items={[
                    {
                      key: 'name',
                      label: '文件',
                      children: subtitleFile.name,
                    },
                    {
                      key: 'size',
                      label: '大小',
                      children: formatByteSize(subtitleFile.size),
                    },
                    {
                      key: 'chars',
                      label: '字符',
                      children:
                        sourceCharCount == null
                          ? '翻译时读取'
                          : sourceCharCount.toLocaleString(),
                    },
                  ]}
                />
              ) : null}

              <Button
                type="primary"
                icon={<TranslationOutlined />}
                block
                size="large"
                disabled={!subtitleFile || !hasValidAnimeId}
                loading={translateMutation.isPending}
                onClick={() => translateMutation.mutate()}
              >
                开始翻译
              </Button>

              {progress ? (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Progress
                    percent={progressPercent}
                    status={
                      translateMutation.isError
                        ? 'exception'
                        : progress.phase === 'done'
                          ? 'success'
                          : 'active'
                    }
                  />
                  <Typography.Text type="secondary">
                    {formatProgressLabel(progress)}
                  </Typography.Text>
                </Space>
              ) : null}

              <Alert
                type="info"
                showIcon
                message="ASS 会按 Dialogue 分片顺序翻译；执行中不要关闭页面。"
              />
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={16}>
          <Card
            title="翻译结果"
            extra={
              /* 手机上收成 icon-only，避免卡头被两颗大按钮挤占 */
              <Space wrap size={isMobile ? 4 : undefined}>
                <Tooltip title={isMobile ? '复制' : undefined}>
                  <Button
                    icon={<CopyOutlined />}
                    aria-label="复制"
                    disabled={!result}
                    onClick={copyResult}
                  >
                    {isMobile ? null : '复制'}
                  </Button>
                </Tooltip>
                <Tooltip title={isMobile ? '下载' : undefined}>
                  <Button
                    icon={<DownloadOutlined />}
                    aria-label="下载"
                    disabled={!result}
                    onClick={downloadResult}
                  >
                    {isMobile ? null : '下载'}
                  </Button>
                </Tooltip>
              </Space>
            }
            style={{ height: '100%' }}
          >
            {result ? (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Descriptions
                  size="small"
                  bordered
                  column={isMobile ? 2 : { xs: 1, md: 4 }}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  items={[
                    {
                      key: 'filename',
                      label: '文件名',
                      children: result.filename,
                    },
                    {
                      key: 'cueCount',
                      label: '条目',
                      children: result.cueCount?.toLocaleString() ?? '-',
                    },
                    {
                      key: 'chunkCount',
                      label: '分片',
                      children: result.chunkCount.toLocaleString(),
                    },
                    {
                      key: 'model',
                      label: '模型',
                      children: result.model ?? '-',
                    },
                  ]}
                />
                <Input.TextArea
                  value={result.text}
                  readOnly
                  rows={isMobile ? 14 : 26}
                  style={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 12,
                    lineHeight: 1.6,
                    whiteSpace: 'pre',
                  }}
                />
              </Space>
            ) : (
              <Empty description="还没有翻译结果" />
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  )
}

async function readTranslateInvokeError(error: unknown): Promise<string> {
  const context = (error as { context?: unknown }).context
  if (context instanceof Response) {
    try {
      const body = (await context.clone().json()) as unknown
      const text = translateErrorBodyText(body)
      if (text) return text
    } catch {
      // Fall back to response text.
    }

    try {
      const text = await context.clone().text()
      if (text.trim()) return text.trim()
    } catch {
      // Fall back to original error.
    }
  }

  if (error instanceof Error) return labelError(error.message)
  return '请求失败'
}

function translateErrorBodyText(body: unknown): string | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  const rec = body as Record<string, unknown>

  const error = stringValue(rec.error)
  const message = stringValue(rec.message)
  const parts: string[] = []

  if (error) parts.push(labelError(error))
  if (message && message !== error) parts.push(labelError(message))
  if (typeof rec.max_chars === 'number') {
    parts.push(`上限 ${rec.max_chars.toLocaleString()} 字符`)
  }
  if (typeof rec.max_cues === 'number') {
    parts.push(`上限 ${rec.max_cues.toLocaleString()} 条`)
  }
  if (typeof rec.got === 'number') {
    parts.push(`当前 ${rec.got.toLocaleString()} 条`)
  }

  return parts.length ? parts.join('；') : null
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function labelError(code: string): string {
  return ERROR_LABELS[code] ?? code
}

function validAssChunkCues(value: number): number {
  return Number.isInteger(value) && value > 0 ? value : DEFAULT_ASS_CHUNK_CUES
}

async function invokeTranslateSubtitle({
  animeId,
  format,
  targetLang,
  subtitle,
  originalFileName,
}: {
  animeId: number
  format: SubtitleFormat
  targetLang: string
  subtitle: string
  originalFileName: string
}): Promise<TranslateInvokeResult> {
  const { data, error, response } = await supabase.functions.invoke<string>(
    'admin-translate-subtitle',
    {
      body: {
        anime_id: animeId,
        format,
        target_lang: targetLang,
        subtitle,
      },
      timeout: TRANSLATE_SUBTITLE_TIMEOUT_MS,
    },
  )

  if (error) throw new Error(await readTranslateInvokeError(error))
  if (typeof data !== 'string' || !data.trim()) {
    throw new Error('响应正文为空')
  }

  return {
    text: data,
    filename:
      parseContentDispositionFilename(
        response?.headers.get('content-disposition'),
      ) ??
      buildFallbackTranslatedFilename(originalFileName, targetLang, format),
    cueCount: parsePositiveIntHeader(response?.headers.get('x-cue-count')),
    model: response?.headers.get('x-model') ?? null,
  }
}

function parsePositiveIntHeader(
  value: string | null | undefined,
): number | null {
  if (!value) return null
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

function formatProgressLabel(progress: TranslateProgress): string {
  switch (progress.phase) {
    case 'reading':
      return '正在读取字幕文件'
    case 'splitting':
      return '正在按 Dialogue 行分片'
    case 'translating':
      if (progress.totalChunks <= 1) {
        return progress.completedChunks === 1
          ? '边缘函数调用完成'
          : '正在调用边缘函数'
      }
      if (progress.activeChunk == null) {
        return `已完成 ${progress.completedChunks}/${progress.totalChunks} 个分片`
      }
      return `正在翻译分片 ${progress.activeChunk}/${progress.totalChunks}（${progress.activeCueCount?.toLocaleString() ?? '-'} 条 Dialogue），已完成 ${progress.completedChunks}/${progress.totalChunks}`
    case 'merging':
      return `正在合并 ${progress.totalChunks} 个分片`
    case 'done':
      return progress.totalChunks > 1
        ? `已完成 ${progress.totalChunks} 个分片`
        : '翻译完成'
    default:
      return ''
  }
}
